import PersianDate from "persian-date";

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

export const toPersianDigits = (value) =>
  String(value ?? "").replace(/\d/g, (d) => FA_DIGITS[d]);

export const toLatinDigits = (value) =>
  String(value ?? "")
    .replace(/[۰-۹]/g, (ch) => String(FA_DIGITS.indexOf(ch)))
    .replace(/[٠-٩]/g, (ch) => String("٠١٢٣٤٥٦٧٨٩".indexOf(ch)));

/** True when the stored string is a Jalali date (year 1300-1500). */
export const isJalaliString = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return false;
  const normalized = toLatinDigits(dateStr.trim());
  const datePart = normalized.split("T")[0];
  const parts = datePart.split(/[/-]/);
  if (parts.length < 3) return false;
  const y = parseInt(parts[0], 10);
  return Number.isFinite(y) && y >= 1300 && y <= 1500;
};

/** Parse "YYYY/MM/DD" or "YYYY-MM-DD" (latin or persian digits) -> {y,m,d} or null. */
export const parseJalaliParts = (jalaliStr) => {
  if (!jalaliStr || typeof jalaliStr !== "string") return null;
  const normalized = toLatinDigits(jalaliStr.trim()).split("T")[0];
  const m = normalized.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isInteger(y) || !Number.isInteger(mo) || !Number.isInteger(d)) return null;
  if (y < 1300 || y > 1500 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, m: mo, d };
};

/** Days in a Jalali month (leap-year aware). */
export const jalaliDaysInMonth = (y, m) => {
  try {
    return new PersianDate([y, m, 1]).daysInMonth();
  } catch {
    if (m <= 6) return 31;
    if (m <= 11) return 30;
    return 29;
  }
};

export const isValidJalali = (jalaliStr) => {
  const p = parseJalaliParts(jalaliStr);
  if (!p) return false;
  return p.d <= jalaliDaysInMonth(p.y, p.m);
};

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Jalali "YYYY/MM/DD" -> Gregorian ISO "YYYY-MM-DD" (backend-safe).
 * Returns "" when invalid. Uses PersianDate array form + local getters
 * (avoids toISOString timezone shifts and the non-existent toGregorian()).
 */
export const jalaliToISO = (jalaliStr) => {
  const p = parseJalaliParts(jalaliStr);
  if (!p || p.d > jalaliDaysInMonth(p.y, p.m)) return "";
  try {
    const d = new PersianDate([p.y, p.m, p.d]).toDate();
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  } catch {
    return "";
  }
};

/** Gregorian ISO / Date -> Jalali "YYYY/MM/DD" (latin digits). Returns "" when invalid. */
export const isoToJalali = (dateStr) => {
  if (!dateStr) return "";
  try {
    if (isJalaliString(dateStr)) {
      const p = parseJalaliParts(dateStr);
      if (!p) return "";
      return `${p.y}/${pad2(p.m)}/${pad2(p.d)}`;
    }
    const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const latin = toLatinDigits(new PersianDate(d).format("YYYY/MM/DD"));
    return latin.match(/^\d{4}\/\d{2}\/\d{2}$/) ? latin : "";
  } catch {
    return "";
  }
};

/** Display string in Jalali with Persian digits, e.g. "۱۴۰۴/۰۶/۱۲". */
export const formatJalaliDate = (dateStr) => {
  if (!dateStr) return "نامشخص";
  try {
    if (isJalaliString(dateStr)) {
      const p = parseJalaliParts(dateStr);
      if (!p) return "نامشخص";
      if (p.d > jalaliDaysInMonth(p.y, p.m)) return "نامشخص";
      return toPersianDigits(`${p.y}/${pad2(p.m)}/${pad2(p.d)}`);
    }
    const jalali = isoToJalali(dateStr);
    if (!jalali) return "نامشخص";
    return toPersianDigits(jalali);
  } catch {
    return "نامشخص";
  }
};

/**
 * Any stored value (Jalali string or Gregorian ISO) -> ISO "YYYY-MM-DD"
 * suitable for <input type=date>-free flows and the backend API.
 */
export const toISODateInput = (dateStr) => {
  if (!dateStr) return "";
  try {
    if (isJalaliString(dateStr)) return jalaliToISO(dateStr);
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  } catch {
    return "";
  }
};

/** Strict ISO "YYYY-MM-DD" validation for API payloads. */
export const isValidISODate = (iso) => {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

export const formatMileage = (mileage) => {
  if (mileage === null || mileage === undefined || mileage === "") return "نامشخص";
  const latin = toLatinDigits(String(mileage)).replace(/\D/g, "");
  if (!latin) return "نامشخص";
  const n = Number(latin);
  if (!Number.isFinite(n)) return "نامشخص";
  return toPersianDigits(n.toLocaleString("en-US")).replace(/,/g, "٬");
};

/**
 * Persian thousands separator used across mileage/KM displays.
 * Example: 1234567 -> "۱٬۲۳۴٬۵۶۷".
 */
export const FA_GROUP_SEP = "٬";

/**
 * Normalize numeric input that may contain Persian/Arabic or Latin digits
 * plus any thousands separators (٬ ، , ، etc).
 * Always returns a display string using Persian digits with a thousands
 * separator every 3 digits ("" when empty).
 * e.g. "1234567" -> "۱٬۲۳۴٬۵۶۷"، "۱۲۳۴۵۶۷" -> "۱٬۲۳۴٬۵۶۷"
 */
export const toPersianNumericDisplay = (raw) => {
  const latin = toLatinDigits(raw ?? "").replace(/\D/g, "");
  if (!latin) return "";
  const trimmed = latin.replace(/^0+(?=\d)/, "");
  const grouped = trimmed.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return toPersianDigits(grouped).replace(/,/g, FA_GROUP_SEP);
};

/** Alias kept for semantic use in mileage/KM fields. */
export const toPersianMileageDisplay = toPersianNumericDisplay;

/** Parse a Persian/Latin numeric string to a Number ("۱۲۳۴۵" -> 12345). NaN when empty. */
export const parsePersianNumber = (raw) => {
  const latin = toLatinDigits(raw ?? "").replace(/\D/g, "");
  if (!latin) return NaN;
  return Number(latin);
};

export const formatCost = (cost) => {
  if (cost === null || cost === undefined || cost === "") return "—";
  const latin = toLatinDigits(String(cost)).replace(/\D/g, "");
  if (!latin) return "—";
  const n = Number(latin);
  if (!Number.isFinite(n)) return "—";
  return `${toPersianDigits(n.toLocaleString("en-US")).replace(/,/g, FA_GROUP_SEP)} تومان`;
};

export const getMechanicName = (service) => {
  const freeText = service?.details?.mechanicName;
  if (typeof freeText === "string" && freeText.trim()) return freeText.trim();
  if (service?.mechanic) {
    const full = `${service.mechanic.firstName || ""} ${service.mechanic.lastName || ""}`.trim();
    if (full) return full;
    return "ثبت شده";
  }
  return "—";
};

/** Newest serviceDate first; falls back to createdAt when unparseable. */
export const sortServicesDesc = (list) => {
  const timeOf = (s) => {
    const iso = toISODateInput(s?.serviceDate);
    if (iso) {
      const t = new Date(`${iso}T12:00:00`).getTime();
      if (!isNaN(t)) return t;
    }
    const c = new Date(s?.createdAt).getTime();
    return isNaN(c) ? 0 : c;
  };
  return [...list].sort((a, b) => timeOf(b) - timeOf(a));
};
